import React, { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";
import ListItem from "@mui/material/ListItem";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";

import Close from "mdi-material-ui/Close";
import FileDocumentOutline from "mdi-material-ui/FileDocumentOutline";

import DefaultPalette from "@core/theme/palettes";
import FormHeader from "views/components/formheader";
import FormButtons from "views/components/formbuttons";
import APICall from "network/APICall";
import EndPoints from "network/Endpoints";
import { Switch } from "@mui/material";
import { isSpaceValidateTextField } from "@core/utils/Const";
import Loader from "@core/helper/Loader";

const customTheme = DefaultPalette();

const Img = styled("img")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    marginRight: theme.spacing(5),
    marginLeft: theme.spacing(2),
  },
  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    width: 200,
  },
}));

const HeadingTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(2),
  },
}));

const AddBanner = (props) => {
  const { open, toggle, type, heading, onSubmitBanner, updateData } = props;
  const [files, setFiles] = useState([]);
  const [filename, setFilename] = useState("");
  const [status, setStatus] = useState(false);

  const theme = useTheme();

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    maxSize: 2000000,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    onDrop: (acceptedFiles) => {
      setFiles(acceptedFiles.map((file) => Object.assign(file)));
      setValue("image", acceptedFiles);
    },
    onDropRejected: () => {
      toast.error("You can only upload 1 file & maximum size of 2 MB.", {
        duration: 2000,
      });
    },
  });

  const renderFilePreview = (file) => {
    if (file.type.startsWith("image")) {
      return (
        <img
          width={38}
          height={38}
          alt={file.name}
          src={URL.createObjectURL(file)}
        />
      );
    } else {
      return <FileDocumentOutline />;
    }
  };

  const handleRemoveFile = () => {
    setFiles([]);
    setValue("image", "");
    setFilename("");
  };

  const fileList = files.map((file) => (
    <ListItem
      key={file.name}
      sx={{ justifyContent: "space-between", display: "flex" }}
    >
      <div
        className="file-details"
        style={{ display: "flex", alignItems: "center", width: "90%" }}
      >
        <div className="file-preview">{renderFilePreview(file)}</div>
        <div style={{ marginLeft: 2, flexDirection: "column", width: "85%" }}>
          <Typography
            className="file-name"
            variant="body2"
            sx={{ wordWrap: "break-word" }}
          >
            {file.name}
          </Typography>
          <Typography className="file-size" variant="body2">
            {Math.round(file.size / 100) / 10 > 1000
              ? `${(Math.round(file.size / 100) / 10000).toFixed(1)} mb`
              : `${(Math.round(file.size / 100) / 10).toFixed(1)} kb`}
          </Typography>
        </div>
      </div>
      <IconButton onClick={() => handleRemoveFile()}>
        <Close fontSize="small" />
      </IconButton>
    </ListItem>
  ));

  const defaultValues = {
    title: "",
    image: "",
    urlLink: "",
    priority: 1,
  };

  const {
    setValue,
    reset,
    control,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues });

  const resetToEmptyValues = useCallback(() => {
    reset();
    setFiles([]);
  }, [reset]);

  const resetToStoredValues = useCallback(() => {
    if (type === "edit") {
      const event = {
        title: updateData.title,
        image: !filename ? updateData.image : filename,
        urlLink: updateData.link,
        priority: updateData.priority,
      };
      setFilename(event.image || "");
      setValue("title", event.title || "");
      setValue("image", event.image || "");
      setValue("urlLink", event.urlLink || "");
      setValue("priority", event.priority || 1);
      setStatus(Number(updateData?.status) === 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, type]);

  useEffect(() => {
    if (type === "edit") {
      resetToStoredValues();
    } else {
      resetToEmptyValues();
    }
  }, [open, resetToStoredValues, resetToEmptyValues, type]);

  const handleClose = () => {
    clearErrors();
    if (type === "add") {
      reset(defaultValues);
    }
    toggle();
  };

  const onSubmit = (data) => {
    const payload = {
      title: data.title,
      priority: data.priority,
    };
    if (data.urlLink) {
      payload.link = data.urlLink;
    }
    Loader.isLoading(true);
    if (type === "add") {
      payload.image = data.image[0];
      APICall("post", payload, EndPoints.addBanner, false, true)
        .then((response) => {
          const { isError, message } = response.data;
          if (!isError) {
            toast.success(message);
            onSubmitBanner();
            handleClose();
          } else {
            toast.error(message);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          Loader.isLoading(false);
        });
    } else {
      payload.id = updateData.id;
      payload.status = status ? 1 : 0;
      if (files.length > 0) {
        payload.image = data.image[0];
      }
      APICall("post", payload, EndPoints.updateBanner, false, true)
        .then((response) => {
          const { isError, message } = response.data;
          if (!isError) {
            toast.success(message);
            onSubmitBanner();
            handleClose();
          } else {
            toast.error(message);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          Loader.isLoading(false);
        });
    }
  };

  return (
    <Drawer
      open={open}
      anchor="right"
      variant="temporary"
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": { width: { xs: 300, sm: 400 } } }}
    >
      <FormHeader heading={heading} onClick={handleClose} />
      <Box sx={{ p: 5 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="title"
                  control={control}
                  rules={{
                    required: true,
                    validate: isSpaceValidateTextField,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Title"
                      error={Boolean(errors.title)}
                      placeholder="Enter title"
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.title && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="urlLink"
                  control={control}
                  rules={{
                    required: false,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Link"
                      placeholder="Enter link"
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.urlLink && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              {files.length === 0 && type === "edit" && filename ? (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderWidth: 1,
                      borderStyle: "dashed",
                      borderColor: customTheme.grey.A200,
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 2,
                      display: "flex",
                      cursor: "pointer",
                    }}
                  >
                    <Img width={100} alt="Upload img" src={updateData.image} />
                    <IconButton onClick={() => handleRemoveFile()}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ) : files.length > 0 ? (
                <div>
                  <Box
                    sx={{
                      borderWidth: 1,
                      borderRadius: 1,
                      borderStyle: "dashed",
                      borderColor: customTheme.grey.A200,
                    }}
                  >
                    <List>{fileList}</List>
                  </Box>
                </div>
              ) : (
                <FormControl fullWidth>
                  <Controller
                    name="image"
                    control={control}
                    rules={{ required: true }}
                    render={() => (
                      <div {...getRootProps({ className: "dropzone" })}>
                        <input {...getInputProps()} />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: ["column", "column", "row"],
                            alignItems: "center",
                            borderWidth: 1,
                            borderRadius: 1,
                            borderStyle: "dashed",
                            borderColor: errors.image
                              ? theme.palette.error.dark
                              : customTheme.grey.A200,
                          }}
                        >
                          <Img
                            width={100}
                            alt="Upload img"
                            src="/images/misc/upload.png"
                          />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              textAlign: ["center", "center", "inherit"],
                            }}
                          >
                            <HeadingTypography>
                              Drop file here or click to upload.
                            </HeadingTypography>
                            <Typography color="textSecondary" variant="body2">
                              Allowed *.jpeg, *.jpg, *.png
                            </Typography>
                            <Typography color="textSecondary" variant="body2">
                              Max 1 file and max size of 2 MB
                            </Typography>
                          </Box>
                        </Box>
                      </div>
                    )}
                  />
                  {errors.image && (
                    <FormHelperText
                      sx={{ color: "error.main" }}
                      id="validation-basic-first-name"
                    >
                      This field is required
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="priority"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="number"
                      label="Priority"
                      inputProps={{
                        min: "1",
                        max: "100",
                      }}
                      error={Boolean(errors.priority)}
                      placeholder="Enter priority"
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.priority && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              {type === "edit" && (
                <Box
                  sx={{ display: "flex", flexDirection: "row", gap: "10px" }}
                >
                  <Switch
                    checked={status}
                    size={"small"}
                    style={{ color: theme.palette.primary.light }}
                    onChange={(e) => {
                      setStatus(e.target.checked);
                    }}
                  />
                  <Typography>Status</Typography>
                </Box>
              )}
            </Grid>
            <FormButtons type={type} resetFunction={resetToEmptyValues} />
          </Grid>
        </form>
      </Box>
    </Drawer>
  );
};

export default AddBanner;
